import type { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { isRedirectableURL } from 'libs/url';
import type { NextRouter } from 'next/router';
import { toast } from 'react-toastify';
import type { OrganizationUsersPermittedServicesType } from 'apis/organization_users/me';
import {
  getOrganizationUsersMe,
  getOrganizationUsersMeLicenses,
} from 'apis/organization_users/me';
import type { OrganizationsLicenseResponse } from 'apis/organizations/sign_in';
import { getTocIsUnread } from 'apis/toc_unreads/is_unread';
import { getUsersMe } from 'apis/users/me';
import { expiredPage } from 'pages/expired/pages';
import { signInOrganizationsPage } from 'pages/pages';
import { usageNotesForUsersPage } from 'pages/usage-notes/pages';
import { httpErrorHandler } from 'utils/httpError';

export const checkUsersMe = (
  router: NextRouter,
  redirectUrl: string
): boolean => {
  // NOTE: セッションチェックのみで、情報は main-layout コンポーネント読み込み時に取得
  getUsersMe()
    .then((response) => {
      if (response.status === 200)
        checkOrganizationUsersMe(router, redirectUrl);
    })
    .catch((error: AxiosError) => {
      const isUnauthorizedError = error.response?.status === 401;
      httpErrorHandler(router, error, {
        noToastMessage: isUnauthorizedError,
        noRedirection: isUnauthorizedError,
      });

      return false;
    });

  return true;
};

export const checkOrganizationUsersMe = (
  router: NextRouter | null,
  redirectUrl: string
) => {
  if (!router?.isReady) return;

  // TODO: Cloud-User の画面ができたら、情報取得箇所を検討する
  getOrganizationUsersMe()
    .then((response) => {
      if (response.status === 200) {
        checkLicencesWithinPeriod().then((isWithinPeriod) => {
          if (isWithinPeriod) {
            const permittedServices = response.data.permittedServices;
            redirectAfterPage(router, redirectUrl, permittedServices);
          } else {
            router?.push({
              pathname: expiredPage.path,
            });
          }
        });
      }
    })
    .catch((error: AxiosError) => {
      if (error.response?.status === 401) {
        router?.push({
          pathname: signInOrganizationsPage.path,
          query: redirectUrl ? { redirect_url: redirectUrl } : {},
        });
        return;
      }

      httpErrorHandler(router, error);
    });
};

export const redirectAfterPage = (
  router: NextRouter | null,
  redirectUrl: string,
  permittedServices: OrganizationUsersPermittedServicesType
) => {
  getTocIsUnread()
    // eslint-disable-next-line sonarjs/cognitive-complexity
    .then((response) => {
      if (response.status === 200) {
        if (response.data.unread) {
          router?.push({
            pathname: usageNotesForUsersPage.path,
            query: redirectUrl ? { redirect_url: redirectUrl } : {},
          });
        } else {
          toast.success('サインインしました。', { delay: 500 });
          const questPermitted = permittedServices.quest;
          const competitionPermitted = permittedServices.competition;
          const dxCopilotPlannerPermitted = permittedServices.dxCopilotPlanner;
          const dxCopilotAnalyticsPermitted =
            permittedServices.dxCopilotAnalytics;
          // サインイン後にリダイレクトパラメータ先へリダイレクト。ない場合は認可サービスに応じて画面遷移
          if (redirectUrl && isRedirectableURL(redirectUrl)) {
            window.location.assign(redirectUrl);
          } else if (questPermitted) {
            router?.push(
              `${process.env.NEXT_PUBLIC_SIGNATE_QUEST_BIZ_URL}/dashboard`
            );
          } else if (competitionPermitted) {
            router?.push(`${process.env.NEXT_PUBLIC_SIGNATE_COMPETITION_URL}`);
          } else if (dxCopilotPlannerPermitted) {
            router?.push(
              `${process.env.NEXT_PUBLIC_SIGNATE_DX_COPILOT_PLANNER_URL}`
            );
          } else if (dxCopilotAnalyticsPermitted) {
            router?.push(
              `${process.env.NEXT_PUBLIC_SIGNATE_DX_COPILOT_ANALYTICS_URL}`
            );
            // eslint-disable-next-line sonarjs/no-duplicated-branches
          } else {
            router?.push(
              `${process.env.NEXT_PUBLIC_SIGNATE_QUEST_BIZ_URL}/dashboard`
            );
          }
        }
      }
    })
    .catch((error: AxiosError) => {
      httpErrorHandler(router, error);
    });
};

const checkLicencesWithinPeriod = async (): Promise<boolean> => {
  try {
    const response = await getOrganizationUsersMeLicenses();
    if (response.status === 200) {
      const licenses = response.data as OrganizationsLicenseResponse[] | null;
      if (!Array.isArray(licenses) || licenses.length === 0) return false;

      const now = dayjs();
      return licenses.some((license) => {
        const startsAt = dayjs(license.startsAt);
        const endsAt = dayjs(license.endsAt);
        return now.isAfter(startsAt) && now.isBefore(endsAt);
      });
    }
    return false;
  } catch (error) {
    return false;
  }
};
